import type { TNormalizedAddress } from "@/domains/address/dto/normalized-address.ts";
import type { IExternalAddress } from "@/domains/address/external-address.ts";
import type { ECountryCode } from "@/domains/country/country.types.ts";
import type { DefaultError } from "@tanstack/vue-query";
import type { MaybeRef } from "vue";

import { useQuery } from "@tanstack/vue-query";
import { computed, ref } from "vue";

import { findCountryDefinitionByCountryCode } from "@/countries/country-definition.ts";
import { NormalizeAddressVisitor } from "@/domains/address/address-visitor.ts";
import {
  acceptAddressProviderVisitor,
  AutocompleteQueryKeyVisitor,
  FindAddressProviderVisitor,
} from "@/domains/address/providers/address-provider-visitor.ts";

export function useAutocompleteAddressQuery(query: MaybeRef<string>, countryCode: MaybeRef<ECountryCode>) {
  const _query = ref(query);
  const _countryCode = ref(countryCode);

  const countryDefinition = computed(() => {
    return findCountryDefinitionByCountryCode(_countryCode.value);
  });

  const queryKey = computed(() => {
    const addressProvider = countryDefinition.value.addressProvider;
    const country = countryDefinition.value.country;
    const fn = acceptAddressProviderVisitor(addressProvider, AutocompleteQueryKeyVisitor);
    return fn(_query.value, country);
  });

  const enabled = computed(() => _query.value.length > 3);

  return useQuery<Array<IExternalAddress<unknown>>, DefaultError, Array<TNormalizedAddress>>({
    staleTime: 1000,
    queryKey: queryKey,
    enabled: enabled,
    queryFn: async () => {
      const addressProvider = acceptAddressProviderVisitor(
        countryDefinition.value.addressProvider,
        FindAddressProviderVisitor,
      );
      return await addressProvider.search(_query.value, countryDefinition.value.country);
    },
    select: (addressSuggestions: IExternalAddress<unknown>[]) => {
      return addressSuggestions.map((address) => address.accept(NormalizeAddressVisitor));
    },
  });
}
